import {
  useAuthenticatedQuery,
  useAuthenticatedMutation,
  type ApiQueryOptions,
  type ApiMutationOptions,
} from '@/hooks/useApi';
import { useQueryClient } from '@tanstack/react-query';
import codesApi from '@/api/codesApi';
import type { ApiResponse, CodeRequestModel } from '@/api/types';

type CodePayload = Record<string, unknown>;
type CodeListResponse = ApiResponse<CodeRequestModel[]>;
type CodeResponse = ApiResponse<CodeRequestModel>;

// Query keys for TNVED code-related queries
export const CODES_KEYS = {
  all: ['codes'] as const,
  byId: (id: string) => ['codes', id] as const,
  byStatus: (status: string) => ['codes', 'status', status] as const,
};

/**
 * Hook for fetching all code requests
 */
export function useCodeRequests(
  options: ApiQueryOptions<CodeListResponse> = {},
) {
  return useAuthenticatedQuery<CodeListResponse>(
    CODES_KEYS.all,
    token => codesApi.getCodes(token),
    options,
  );
}

/**
 * Hook for fetching a specific code request by ID
 */
export function useCodeRequest(
  id: string,
  options: ApiQueryOptions<CodeResponse> = {},
) {
  return useAuthenticatedQuery<CodeResponse>(
    CODES_KEYS.byId(id),
    token => codesApi.getCodeById(id, token),
    {
      enabled: !!id,
      ...options,
    },
  );
}

/**
 * Hook for fetching a specific code by ID (alias matching API)
 */
export function useCode(
  id: string,
  options: ApiQueryOptions<CodeResponse> = {},
) {
  return useAuthenticatedQuery<CodeResponse>(
    CODES_KEYS.byId(id),
    token => codesApi.getCodeById(id, token),
    {
      enabled: !!id,
      ...options,
    },
  );
}

/**
 * Hook for creating a new code request
 */
export function useCreateCodeRequest(
  options: ApiMutationOptions<CodeResponse, { codeData: CodePayload }> = {},
) {
  const queryClient = useQueryClient();

  return useAuthenticatedMutation<CodeResponse, { codeData: CodePayload }>(
    ({ codeData, token }) => codesApi.createCode(codeData, token),
    {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: CODES_KEYS.all });
      },
      ...options,
    },
  );
}

/**
 * Hook for creating a new code (alias for createCode API method)
 */
export function useCreateCode(
  options: ApiMutationOptions<CodeResponse, { data: CodePayload }> = {},
) {
  const queryClient = useQueryClient();

  return useAuthenticatedMutation<CodeResponse, { data: CodePayload }>(
    ({ data, token }) => codesApi.createCode(data, token),
    {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: CODES_KEYS.all });
      },
      ...options,
    },
  );
}

/**
 * Hook for updating a code request
 */
export function useUpdateCodeRequest(
  options: ApiMutationOptions<
    CodeResponse,
    { id: string; codeData: CodePayload }
  > = {},
) {
  const queryClient = useQueryClient();

  return useAuthenticatedMutation<
    CodeResponse,
    { id: string; codeData: CodePayload }
  >(({ id, codeData, token }) => codesApi.updateCode(id, codeData, token), {
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: CODES_KEYS.byId(variables.id),
      });
      queryClient.invalidateQueries({ queryKey: CODES_KEYS.all });
    },
    ...options,
  });
}

/**
 * Hook for updating a code (edit mode)
 */
export function useUpdateCode(
  options: ApiMutationOptions<
    CodeResponse,
    { id: string; codeData: CodePayload }
  > = {},
) {
  const queryClient = useQueryClient();

  return useAuthenticatedMutation<
    CodeResponse,
    { id: string; codeData: CodePayload }
  >(({ id, codeData, token }) => codesApi.updateCode(id, codeData, token), {
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: CODES_KEYS.byId(variables.id),
      });
      queryClient.invalidateQueries({ queryKey: CODES_KEYS.all });
    },
    ...options,
  });
}

/**
 * Hook for submitting code request feedback
 */
export function useSubmitCodeFeedback(
  options: ApiMutationOptions<
    CodeResponse,
    { feedbackData: CodePayload & { code_request_id?: string } }
  > = {},
) {
  const queryClient = useQueryClient();

  return useAuthenticatedMutation<
    CodeResponse,
    { feedbackData: CodePayload & { code_request_id?: string } }
  >(({ feedbackData, token }) => codesApi.submitFeedback(feedbackData, token), {
    onSuccess: (_data, variables) => {
      if (variables.feedbackData.code_request_id) {
        queryClient.invalidateQueries({
          queryKey: CODES_KEYS.byId(variables.feedbackData.code_request_id),
        });
      }
    },
    ...options,
  });
}

/**
 * Hook for accepting code request payment
 */
export function useAcceptCode(
  options: ApiMutationOptions<CodeResponse, { codeRequestId: string }> = {},
) {
  const queryClient = useQueryClient();

  return useAuthenticatedMutation<CodeResponse, { codeRequestId: string }>(
    ({ codeRequestId, token }) => codesApi.acceptCode(codeRequestId, token),
    {
      onSuccess: (_data, variables) => {
        queryClient.invalidateQueries({
          queryKey: CODES_KEYS.byId(variables.codeRequestId),
        });
        queryClient.invalidateQueries({ queryKey: CODES_KEYS.all });
      },
      ...options,
    },
  );
}

/**
 * Hook for submitting code invoice (facture)
 */
export function useSubmitCodeFacture(
  options: ApiMutationOptions<CodeResponse, { factureData: CodePayload }> = {},
) {
  const queryClient = useQueryClient();

  return useAuthenticatedMutation<CodeResponse, { factureData: CodePayload }>(
    ({ factureData, token }) => codesApi.submitFacture(factureData, token),
    {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: CODES_KEYS.all });
      },
      ...options,
    },
  );
}

/**
 * Hook for completing a code request
 */
export function useCompleteCodeRequest(
  options: ApiMutationOptions<
    CodeResponse,
    { completionData: CodePayload }
  > = {},
) {
  const queryClient = useQueryClient();

  return useAuthenticatedMutation<
    CodeResponse,
    { completionData: CodePayload }
  >(
    ({ completionData, token }) =>
      codesApi.completeRequest(completionData, token),
    {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: CODES_KEYS.all });
      },
      ...options,
    },
  );
}

/**
 * Hook for submitting code cancellation comment
 */
export function useSubmitCodeCancellation(
  options: ApiMutationOptions<
    CodeResponse,
    { codeRequestId: string; comment: string }
  > = {},
) {
  const queryClient = useQueryClient();

  return useAuthenticatedMutation<
    CodeResponse,
    { codeRequestId: string; comment: string }
  >(
    ({ codeRequestId, comment, token }) =>
      codesApi.submitCancellationComment(codeRequestId, comment, token),
    {
      onSuccess: (_data, variables) => {
        queryClient.invalidateQueries({
          queryKey: CODES_KEYS.byId(variables.codeRequestId),
        });
        queryClient.invalidateQueries({ queryKey: CODES_KEYS.all });
      },
      ...options,
    },
  );
}
